import React, { useState, useEffect } from 'react'
import { Plus, Trash2, ArrowUp, ArrowDown, Package, Save, AlertCircle } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { sansJpa, stagesDesJpa } from '../../../lib/jpa/stages.supabase'

interface SupplyItem {
  id: string
  periode: string | null
  label: string
  category: 'commun' | 'general' | 'petits'
  sort_order: number
  active: boolean
}

type ActiveSection = 'commun' | 'petits' | string

export function SuppliesManagement() {
  const [supplies, setSupplies] = useState<SupplyItem[]>([])
  const [periodes, setPeriodes] = useState<string[]>([])
  const [activeSection, setActiveSection] = useState<ActiveSection>('commun')
  const [loading, setLoading] = useState(true)
  const [newItemLabel, setNewItemLabel] = useState('')
  const [saving, setSaving] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [newPeriodeName, setNewPeriodeName] = useState('')
  const [showAddPeriode, setShowAddPeriode] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const jpa = await stagesDesJpa()
      const [suppliesRes, periodesRes] = await Promise.all([
        supabase
          .from('session_supplies')
          .select('*')
          .order('sort_order'),
        sansJpa(supabase
          .from('sessions')
          .select('periode'), jpa)
      ])

      setSupplies(suppliesRes.data || [])

      const uniquePeriodes = [...new Set((periodesRes.data || []).map(s => s.periode))].sort()
      // Also include periodes from supplies that might not be in sessions
      const suppliesPeriodes = [...new Set((suppliesRes.data || []).filter(s => s.periode).map(s => s.periode!))]
      const allPeriodes = [...new Set([...uniquePeriodes, ...suppliesPeriodes])].sort()
      setPeriodes(allPeriodes)
    } catch (err) {
      console.error('Error fetching supplies data:', err)
    } finally {
      setLoading(false)
    }
  }

  const getItemsForSection = (): SupplyItem[] => {
    if (activeSection === 'commun') {
      return supplies.filter(s => s.category === 'commun').sort((a, b) => a.sort_order - b.sort_order)
    }
    if (activeSection === 'petits') {
      return supplies.filter(s => s.category === 'petits').sort((a, b) => a.sort_order - b.sort_order)
    }
    return supplies.filter(s => s.category === 'general' && s.periode === activeSection).sort((a, b) => a.sort_order - b.sort_order)
  }

  const addItem = async () => {
    if (!newItemLabel.trim()) return
    setSaving(true)
    setErrorMsg('')

    const items = getItemsForSection()
    const maxSort = items.length > 0 ? Math.max(...items.map(i => i.sort_order)) : 0

    const newItem: Partial<SupplyItem> = {
      label: newItemLabel.trim(),
      sort_order: maxSort + 1,
      active: true
    }

    if (activeSection === 'commun') {
      newItem.periode = null
      newItem.category = 'commun'
    } else if (activeSection === 'petits') {
      newItem.periode = null
      newItem.category = 'petits'
    } else {
      newItem.periode = activeSection
      newItem.category = 'general'
    }

    const { error } = await supabase.from('session_supplies').insert(newItem)

    if (error) {
      setErrorMsg('Erreur lors de l\'ajout')
      console.error(error)
    } else {
      setNewItemLabel('')
      await fetchData()
      showSuccess('Item ajoute')
    }
    setSaving(false)
  }

  const deleteItem = async (id: string) => {
    if (!confirm('Supprimer cet item ?')) return

    const { error } = await supabase.from('session_supplies').delete().eq('id', id)
    if (error) {
      setErrorMsg('Erreur lors de la suppression')
    } else {
      await fetchData()
      showSuccess('Item supprime')
    }
  }

  const toggleActive = async (item: SupplyItem) => {
    const { error } = await supabase
      .from('session_supplies')
      .update({ active: !item.active })
      .eq('id', item.id)

    if (error) {
      setErrorMsg('Erreur lors de la mise a jour')
    } else {
      await fetchData()
    }
  }

  const moveItem = async (item: SupplyItem, direction: 'up' | 'down') => {
    const items = getItemsForSection()
    const idx = items.findIndex(i => i.id === item.id)
    if (idx < 0) return
    if (direction === 'up' && idx === 0) return
    if (direction === 'down' && idx === items.length - 1) return

    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    const otherItem = items[swapIdx]

    await Promise.all([
      supabase.from('session_supplies').update({ sort_order: otherItem.sort_order }).eq('id', item.id),
      supabase.from('session_supplies').update({ sort_order: item.sort_order }).eq('id', otherItem.id)
    ])

    await fetchData()
  }

  const updateLabel = async (id: string, newLabel: string) => {
    const { error } = await supabase
      .from('session_supplies')
      .update({ label: newLabel })
      .eq('id', id)

    if (error) {
      setErrorMsg('Erreur lors de la mise a jour')
    } else {
      await fetchData()
      showSuccess('Item mis a jour')
    }
  }

  const addNewPeriode = async () => {
    if (!newPeriodeName.trim()) return
    const name = newPeriodeName.trim()
    if (periodes.includes(name)) {
      setErrorMsg('Cette periode existe deja')
      return
    }

    // Insert a placeholder item for this new period
    const { error } = await supabase.from('session_supplies').insert({
      periode: name,
      label: 'Vetements adaptes a la saison',
      category: 'general',
      sort_order: 1,
      active: true
    })

    if (error) {
      setErrorMsg('Erreur lors de la creation')
    } else {
      setNewPeriodeName('')
      setShowAddPeriode(false)
      await fetchData()
      setActiveSection(name)
      showSuccess(`Periode "${name}" ajoutee`)
    }
  }

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg)
    setTimeout(() => setSuccessMsg(''), 3000)
  }

  const sectionLabel = () => {
    if (activeSection === 'commun') return 'Items communs (affiches pour toutes les periodes)'
    if (activeSection === 'petits') return 'Items pour les plus petits (2,5-3,5 ans - Poussins)'
    return `Items specifiques a la periode "${activeSection}"`
  }

  const items = getItemsForSection()

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Messages */}
      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 font-medium">
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 font-medium flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          {errorMsg}
          <button onClick={() => setErrorMsg('')} className="ml-auto text-red-600 hover:text-red-800 text-xs">Fermer</button>
        </div>
      )}

      {/* Section tabs */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveSection('commun')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeSection === 'commun'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Commun
        </button>

        {periodes.map(p => (
          <button
            key={p}
            onClick={() => setActiveSection(p)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeSection === p
                ? 'bg-amber-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {p}
          </button>
        ))}

        <button
          onClick={() => setActiveSection('petits')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeSection === 'petits'
              ? 'bg-pink-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Petits (2,5-3,5 ans)
        </button>

        {/* Add period button */}
        <button
          onClick={() => setShowAddPeriode(!showAddPeriode)}
          className="px-3 py-2 rounded-lg text-sm font-medium bg-gray-50 text-gray-500 hover:bg-gray-100 border border-dashed border-gray-300 transition-colors"
        >
          <Plus className="h-4 w-4 inline -mt-0.5 mr-1" />
          Periode
        </button>
      </div>

      {/* Add new period input */}
      {showAddPeriode && (
        <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
          <input
            type="text"
            value={newPeriodeName}
            onChange={e => setNewPeriodeName(e.target.value)}
            placeholder="Nom de la nouvelle periode (ex: Noel)"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            onKeyDown={e => e.key === 'Enter' && addNewPeriode()}
          />
          <button
            onClick={addNewPeriode}
            disabled={!newPeriodeName.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            Ajouter
          </button>
          <button
            onClick={() => { setShowAddPeriode(false); setNewPeriodeName('') }}
            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
          >
            Annuler
          </button>
        </div>
      )}

      {/* Section header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{sectionLabel()}</h3>
          <p className="text-xs text-gray-500 mt-1">
            {activeSection === 'commun'
              ? 'Ces items apparaissent dans tous les emails peu importe la periode de la session.'
              : activeSection === 'petits'
                ? 'Ces items apparaissent dans une section separee "Pour les plus petits" dans tous les emails.'
                : `Ces items n'apparaissent que pour les sessions de la periode "${activeSection}".`}
          </p>
        </div>
        <span className="text-sm text-gray-400">{items.length} item{items.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Items list */}
      <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">
            Aucun item configure pour cette section. Ajoutez-en un ci-dessous.
          </div>
        ) : (
          items.map((item, idx) => (
            <ItemRow
              key={item.id}
              item={item}
              isFirst={idx === 0}
              isLast={idx === items.length - 1}
              onDelete={deleteItem}
              onToggleActive={toggleActive}
              onMove={moveItem}
              onUpdateLabel={updateLabel}
            />
          ))
        )}
      </div>

      {/* Add item input */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={newItemLabel}
          onChange={e => setNewItemLabel(e.target.value)}
          placeholder="Ajouter un nouvel item..."
          className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          onKeyDown={e => e.key === 'Enter' && addItem()}
        />
        <button
          onClick={addItem}
          disabled={!newItemLabel.trim() || saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Ajouter
        </button>
      </div>

      {/* Preview info */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-800">
          <strong>Apercu dans l'email :</strong> Les items sont affiches dans cet ordre : items communs, puis items specifiques a la periode, puis items pour les petits.
          Les items desactives (gris) ne sont pas affiches dans l'email.
        </p>
      </div>
    </div>
  )
}

function ItemRow({
  item,
  isFirst,
  isLast,
  onDelete,
  onToggleActive,
  onMove,
  onUpdateLabel
}: {
  item: SupplyItem
  isFirst: boolean
  isLast: boolean
  onDelete: (id: string) => void
  onToggleActive: (item: SupplyItem) => void
  onMove: (item: SupplyItem, direction: 'up' | 'down') => void
  onUpdateLabel: (id: string, label: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [editLabel, setEditLabel] = useState(item.label)

  const handleSave = () => {
    if (editLabel.trim() && editLabel !== item.label) {
      onUpdateLabel(item.id, editLabel.trim())
    }
    setEditing(false)
  }

  return (
    <div className={`flex items-center gap-3 px-4 py-3 group ${!item.active ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}`}>
      {/* Reorder buttons */}
      <div className="flex flex-col gap-0.5">
        <button
          onClick={() => onMove(item, 'up')}
          disabled={isFirst}
          className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ArrowUp className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => onMove(item, 'down')}
          disabled={isLast}
          className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ArrowDown className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Label */}
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            type="text"
            value={editLabel}
            onChange={e => setEditLabel(e.target.value)}
            onBlur={handleSave}
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false) }}
            className="w-full border border-blue-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        ) : (
          <p
            onClick={() => { setEditing(true); setEditLabel(item.label) }}
            className="text-sm text-gray-900 truncate cursor-pointer hover:text-blue-600"
            title="Cliquer pour modifier"
          >
            {item.label.replace(/<[^>]*>/g, '')}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onToggleActive(item)}
          className={`p-1 rounded ${item.active ? 'text-green-600 hover:text-green-800' : 'text-gray-400 hover:text-gray-600'}`}
          title={item.active ? 'Desactiver' : 'Activer'}
        >
          {item.active ? <Save className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
        </button>
        <button
          onClick={() => onDelete(item.id)}
          className="p-1 text-red-400 hover:text-red-600 rounded"
          title="Supprimer"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
